import qs from "query-string";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  CoursesRoutes,
  ensureTrailingSlash,
  generateSpecificSearchPattern,
  getBaseUrl,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  tableSearchAF,
  usePrefixedRouting,
} from "@ryde/shared";

import { useDynamicFormsFetch } from "../use-dynamic-forms-fetch";

const FETCH_COURSES_DELAY_MS = 100;

type TCoursesByIdsNavigation = {
  navigateToCoursesByIds: (courseIds: number[]) => void;
  openCoursesByIdsInNewTab: (courseIds: number[]) => void;
};

export const useCoursesByIdsNavigation = (): TCoursesByIdsNavigation => {
  const { navigatePrefixed, addPrefix } = usePrefixedRouting();
  const setTableSearch = useSetRecoilState(tableSearchAF("course"));
  const setStarredDisplayMode = useSetRecoilState(
    starredDisplayModeAtomFamily("course"),
  );
  // Courses are NOT in `ServerFilteredEntities` and have no `POST /courses/search` route, so the fetch has
  // to go through the entity-aware entry point — the server-filtered one 404s.
  const { fetchTableRows: fetchCourses } = useDynamicFormsFetch("course");

  const navigateToCoursesByIds = useEvent((courseIds: number[]): void => {
    if (!courseIds?.length) {
      return;
    }
    setStarredDisplayMode(StarredDisplayModeEnum.ShowAllIncludeStarred);
    setTableSearch(generateSpecificSearchPattern(courseIds));
    navigatePrefixed(CoursesRoutes.SEARCH_GRID);
    setTimeout(() => fetchCourses(), FETCH_COURSES_DELAY_MS);
  });

  /**
   * Opens courses grid with `id:` search in a new browser tab.
   * Hash URL + `search` QS is hydrated by the grid on load (same idea as produce results).
   */
  const openCoursesByIdsInNewTab = useEvent((courseIds: number[]): void => {
    if (!courseIds?.length) {
      return;
    }
    const searchPattern = generateSpecificSearchPattern(courseIds);
    const relativePath = addPrefix(
      `${CoursesRoutes.SEARCH_GRID}?${qs.stringify({ search: searchPattern })}`,
    );
    const coursesUrl = `${ensureTrailingSlash(getBaseUrl())}#${relativePath}`;
    window.open(coursesUrl, "_blank");
  });

  const navigation: TCoursesByIdsNavigation = {
    navigateToCoursesByIds,
    openCoursesByIdsInNewTab,
  };
  return navigation;
};
